import React from 'react'
import { SelectChangeEvent } from '@mui/material'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import SvgIcon from '@mui/material/SvgIcon'

import { useBooleanState } from '../../hooks/useBooleanState'
import { SelectInputProps } from './SelectInput.types'
import { useStyles } from './SelectInput.styles'
import { ReactComponent as ArrowDownIcon } from '../../../assets/images/arrows/down-bold.svg'
import { ReactComponent as CheckedIcon } from '../../../assets/images/checkbox-checked.svg'
import { ReactComponent as UncheckedIcon } from '../../../assets/images/checkbox-unchecked.svg'
import {
    getOptionById,
    isMultipleSelect,
    isOptionSelected,
    isSingleSelect,
    renderValueForMultiple,
    renderValueForSingle,
} from './SelectInput.helpers'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import Stack from '@mui/material/Stack'
import { MenuItem } from '@mui/material/'

const SelectInputIcon = ({ className }: { className: string }) => (
    <SvgIcon
        className={className}
        component={ArrowDownIcon}
        inheritViewBox
        color="inherit"
        sx={{ fontSize: '16px', right: '16px !important', color: 'inherit !important' }}
    />
)

const CheckboxUncheckedIcon = (
    <SvgIcon component={UncheckedIcon} inheritViewBox sx={{ fontSize: '16px' }} color="inherit" />
)
const CheckboxCheckedIcon = <SvgIcon component={CheckedIcon} inheritViewBox sx={{ fontSize: '16px' }} color="inherit" />

const SELECT_ALL_VALUE = 'select-all'

export const SelectInput = (props: SelectInputProps) => {
    const { options, placeholder = 'Select option' } = props
    const styles = useStyles()
    const open = useBooleanState()

    const isAllDeselected = isMultipleSelect(props) ? props.selected.length === 0 : !props.selected

    const handleToggleSelectAll = () => {
        if (isMultipleSelect(props)) {
            if (isAllDeselected) {
                props.onChange(options.map((option) => option.id))
            } else {
                props.onChange([])
            }
        }
    }

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        if (isMultipleSelect(props)) {
            const value = event.target.value as string[]

            if (value.at(-1) === SELECT_ALL_VALUE) {
                return handleToggleSelectAll()
            }

            props.onChange(value)
        } else {
            const value = event.target.value as string
            props.onChange(value)
        }
    }

    const renderValue = (selected: SelectInputProps['selected']) => {
        const value = isMultipleSelect(props)
            ? renderValueForMultiple(selected, (id) => getOptionById(options, id), placeholder, options.length)
            : renderValueForSingle(selected, (id) => getOptionById(options, id), placeholder)

        return (
            <Typography variant="16x600" color="figma.primary">
                {value}
            </Typography>
        )
    }

    const handleOptionClick = (optionId: string) => {
        if (isSingleSelect(props) && isOptionSelected(props, optionId) && !props.required) {
            props.onChange('')
        }
    }

    return (
        <FormControl fullWidth>
            <Select
                MenuProps={{
                    ...props.MenuProps,
                    classes: {
                        paper: styles.paper,
                        list: styles.list,
                    },
                }}
                open={open.state}
                onClose={open.handleSetAsFalse}
                onOpen={open.handleSetAsTrue}
                variant="outlined"
                multiple={props.multiple}
                disabled={props.disabled}
                value={props.selected}
                onChange={handleChange}
                displayEmpty
                renderValue={renderValue}
                IconComponent={SelectInputIcon}
                sx={(theme) => ({
                    borderRadius: '16px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: `1px !important`,
                        borderColor: `${theme.palette.figma.grayscale.stroke} !important`,
                    },
                    '& .MuiOutlinedInput-input': {
                        p: 2,
                    },
                    '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${theme.palette.figma.grayscale.tier5} !important`,
                        },
                    },
                    ...(open.state
                        ? {
                              borderRadius: '20px',
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                              backgroundColor: 'figma.grayscale.tier7',
                              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20)',
                              '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'unset !important',
                              },
                          }
                        : {}),
                })}
            >
                {isMultipleSelect(props) && (
                    <MenuItem
                        value={SELECT_ALL_VALUE}
                        disableRipple
                        sx={() => ({
                            flex: 1,
                            p: 0,
                            borderRadius: '8px',
                            color: 'figma.primary',
                            backgroundColor: 'unset !important',
                        })}
                    >
                        <Typography variant="16x600" color="inherit" sx={{ mt: -0.25 }}>
                            {isAllDeselected ? 'Select all' : 'Deselect all'}
                        </Typography>
                    </MenuItem>
                )}
                {options.map((option) => {
                    const isSelected = isOptionSelected(props, option.id)
                    const isMultiple = isMultipleSelect(props)

                    const handleClick = () => {
                        handleOptionClick(option.id)
                    }
                    return (
                        <MenuItem
                            key={option.id}
                            selected={isSelected}
                            onClick={handleClick}
                            value={option.id}
                            disableRipple
                            sx={(theme) => ({
                                flex: 1,
                                p: 0,
                                borderRadius: '8px',
                                color: isSelected ? 'figma.primary' : 'figma.grayscale.textHier2',
                                ...(isMultiple
                                    ? {
                                          backgroundColor: 'unset !important',
                                      }
                                    : {
                                          backgroundColor: isSelected
                                              ? `${theme.palette.figma.primary2} !important`
                                              : 'unset !important',
                                          mx: isSelected ? -1 : 0,
                                          my: isSelected ? -0.5 : 0,
                                          px: isSelected ? 1 : 0,
                                          py: isSelected ? 0.5 : 0,
                                      }),
                                '&:hover': {
                                    color: 'figma.primary',
                                },
                            })}
                        >
                            <Stack direction="row" gap={1}>
                                {isMultiple && (
                                    <Checkbox
                                        size="small"
                                        checked={isSelected}
                                        icon={CheckboxUncheckedIcon}
                                        checkedIcon={CheckboxCheckedIcon}
                                        sx={{
                                            pointerEvents: 'none',
                                            p: 0,
                                            color: 'inherit',
                                        }}
                                    />
                                )}
                                <Typography variant="16x500" color="inherit" sx={{ mt: -0.25 }}>
                                    {option.label}
                                </Typography>
                            </Stack>
                        </MenuItem>
                    )
                })}
            </Select>
        </FormControl>
    )
}
